/**
 * Canonical failure metadata for capture documents (frontend mirror of functions/src/failureMetadata.ts).
 */

import { deleteField } from 'firebase/firestore';
import { timestampToMs } from './pipeline/pipelineTimestampUtils';
import { readCaptureFrameFields } from './pipeline/pipelineFrameFields';
import { PIPELINE_STAGE } from '../constants/pipelineStages';



export const CANONICAL_FAILED_AT = {

  UPLOAD: 'Video Upload',

  FRAME_EXTRACTION: 'Image Extraction',

  SFM: 'SFM',

  GAUSSIAN_TRAINING: 'Gaussian Training',

  SOG_CONVERSION: 'SOG conversion',

  DISPATCH: 'Dispatch',

  CANCELLED: 'Cancelled',

  UNKNOWN: 'Unknown',

};



/** Zero-based pipeline stage index from holistic percent. */

export function failedStageIndexFromPercent(pct) {

  const p = Number.isFinite(pct) ? Math.max(0, Math.min(100, pct)) : 0;

  if (p >= 92) return 3;

  if (p >= 51) return 3;

  if (p >= 5) return 2;

  if (p >= 3) return 1;

  return 0;

}

/** True for generic watchdog / RunPod ghost-complete copy without a concrete stage label. */
export function isWatchdogGenericFailureDetail(detail) {
  const d = String(detail || '').trim().toLowerCase();
  if (!d) return false;
  return (
    d.includes('worker finished without delivering results')
    || d.includes('maximum processing time')
    || d.includes('dispatch was lost')
    || d.includes('without delivering results')
    || d.includes('pipeline progress stalled')
    || d.includes('finalization did not reach viewer-ready')
  );
}

/**
 * Infer terminal failure stage from Firestore processing context — not percent alone.
 */
export function resolveFailureStageFromProcessingContext({
  proc = {},
  progressPercent = null,
  progressMessage = null,
  inferredRedisStage = null,
} = {}) {
  const procStage = String(proc.stage || '').trim().toLowerCase();
  const { framesExtracted, framesRegistered } = readCaptureFrameFields(proc);
  const pct = Number.isFinite(progressPercent) ?
    Math.max(0, Math.min(100, Math.round(progressPercent))) :
    0;
  const msg = String(progressMessage || '').toLowerCase();
  const inferred = String(inferredRedisStage || '').trim().toLowerCase();

  if (procStage === PIPELINE_STAGE.RENDERING) {
    return { failedAtRaw: CANONICAL_FAILED_AT.GAUSSIAN_TRAINING, failedStageIndex: 3 };
  }
  if (procStage === PIPELINE_STAGE.BUILDING_STRUCTURE) {
    return { failedAtRaw: CANONICAL_FAILED_AT.SFM, failedStageIndex: 2 };
  }

  if (
    msg.includes('structure from motion')
    || msg.includes('matching camera')
    || msg.includes('analyzing images')
    || msg.includes('sfm')
    || msg.includes('3d reconstruction')
    || msg.includes('colmap')
  ) {
    return { failedAtRaw: CANONICAL_FAILED_AT.SFM, failedStageIndex: 2 };
  }
  if (msg.includes('gaussian') || msg.includes('generating')) {
    return { failedAtRaw: CANONICAL_FAILED_AT.GAUSSIAN_TRAINING, failedStageIndex: 3 };
  }

  if (inferred === 'sfm') {
    return { failedAtRaw: CANONICAL_FAILED_AT.SFM, failedStageIndex: 2 };
  }
  if (inferred === 'gaussiantraining' || inferred === 'finalizing') {
    return { failedAtRaw: CANONICAL_FAILED_AT.GAUSSIAN_TRAINING, failedStageIndex: 3 };
  }

  const extracted = Number.isFinite(framesExtracted) ? framesExtracted : null;
  const registered = Number.isFinite(framesRegistered) ? framesRegistered : null;

  if (extracted != null && extracted > 0) {
    if (registered != null && registered >= 3 && pct >= 51) {
      return { failedAtRaw: CANONICAL_FAILED_AT.GAUSSIAN_TRAINING, failedStageIndex: 3 };
    }
    return { failedAtRaw: CANONICAL_FAILED_AT.SFM, failedStageIndex: 2 };
  }

  if (inferred === 'video2images' || (pct >= 3 && pct < 5)) {
    return { failedAtRaw: CANONICAL_FAILED_AT.FRAME_EXTRACTION, failedStageIndex: 1 };
  }

  const idx = failedStageIndexFromPercent(pct);
  return { failedAtRaw: canonicalLabelFromStageIndex(idx), failedStageIndex: idx };
}

export function canonicalLabelFromStageIndex(idx) {

  switch (idx) {

    case 0: return CANONICAL_FAILED_AT.UPLOAD;

    case 1: return CANONICAL_FAILED_AT.FRAME_EXTRACTION;

    case 2: return CANONICAL_FAILED_AT.SFM;

    case 3: return CANONICAL_FAILED_AT.GAUSSIAN_TRAINING;

    default: return CANONICAL_FAILED_AT.UNKNOWN;

  }

}



export function failedStageIndexFromLabel(label) {

  const n = (label || '').trim().toLowerCase();

  if (!n || n === 'cancelled' || n === 'dispatch') return -1;

  if (n === 'upload' || n === 'video upload' || n.includes('uploadpending')) return 0;

  if (
    n === 'frame extraction'
    || n === 'image extraction'
    || (n.includes('extract') && (n.includes('frame') || n.includes('image')))
  ) return 1;

  if (n === 'video2images') return 1;

  if (n === 'sfm' || n.includes('structure from motion') || n.includes('3d reconstruction')) return 2;

  if (n.includes('gaussian') || n.includes('export') || n.includes('generating')) return 3;
  if (n.includes('sog') || n.includes('finalizing') || n.includes('viewer ready')) return 3;

  if (n.includes('semantic')) return 3;

  return -1;

}



export function isCanonicalFailedAtLabel(value) {

  const s = String(value || '').trim();

  return Object.values(CANONICAL_FAILED_AT).includes(s);

}



export function isVerboseFailureText(value) {

  const s = String(value || '').trim();

  if (!s) return false;

  if (isCanonicalFailedAtLabel(s)) return false;

  if (failedStageIndexFromLabel(s) >= 0) return false;

  return true;

}



/** Preserve specific user-facing failure detail instead of overwriting with watchdog generic text. */
export function isSpecificUserFacingFailureDetail(failureDetail, failedAt) {
  const detail = String(failureDetail || '').trim();
  if (!detail || !isVerboseFailureText(detail)) return false;
  const lower = detail.toLowerCase();
  const at = String(failedAt || '').trim().toLowerCase();
  if (lower.includes('too sparse')) return true;
  if (lower.includes('images registered')) return true;
  if (lower.includes('parallax/overlap')) return true;
  if (lower.includes('frame') && lower.includes('extract')) return true;
  if (at === 'upload' || (lower.includes('upload') && !lower.includes('uploading new splat'))) return true;
  if (lower.includes('storage') && (lower.includes('full') || lower.includes('cap') || lower.includes('quota'))) {
    return true;
  }
  if (
    lower.includes('sfm')
    || lower.includes('colmap')
    || lower.includes('structure from motion')
    || lower.includes('3d reconstruction')
  ) {
    return true;
  }
  return false;
}



function stageFromVerboseFailureText(text) {

  const lower = text.toLowerCase();

  if (

    lower.includes('too sparse')

    || lower.includes('parallax/overlap')

    || lower.includes('images registered')

    || (lower.includes('too few') && lower.includes('3d point'))

    || lower.includes('points3d')

    || lower.includes('transforms.json')

    || lower.includes('no usable camera')

    || lower.startsWith('sfm ')

    || lower.includes(' colmap')

    || lower.includes('structure from motion')

    || lower.includes('3d reconstruction')

  ) {

    return CANONICAL_FAILED_AT.SFM;

  }

  if (lower.includes('frame') && lower.includes('extract')) {

    return CANONICAL_FAILED_AT.FRAME_EXTRACTION;

  }

  if (lower.includes('gaussian') || lower.includes('generating')) {

    return CANONICAL_FAILED_AT.GAUSSIAN_TRAINING;

  }

  if (lower.includes('upload')) return CANONICAL_FAILED_AT.UPLOAD;

  if (lower.includes('no healthy gpu') || lower.includes('gpu worker')) {

    return CANONICAL_FAILED_AT.DISPATCH;

  }

  return null;

}



export function canonicalFailedAtFromRaw(raw, options = {}) {

  const s = (raw || '').trim();

  const lower = s.toLowerCase();



  if (lower === 'cancelled') return CANONICAL_FAILED_AT.CANCELLED;

  if (lower === 'dispatch') return CANONICAL_FAILED_AT.DISPATCH;

  if (lower === 'upload' || lower.includes('uploadpending')) return CANONICAL_FAILED_AT.UPLOAD;

  if (lower.includes('frame') && lower.includes('extract')) return CANONICAL_FAILED_AT.FRAME_EXTRACTION;

  if (lower === 'video2images') return CANONICAL_FAILED_AT.FRAME_EXTRACTION;

  if (lower === 'sfm' || lower.includes('structure from motion') || lower.includes('3d reconstruction')) {

    return CANONICAL_FAILED_AT.SFM;

  }

  if (lower.includes('gaussian') || lower.includes('export') || lower.includes('generating')) {

    return CANONICAL_FAILED_AT.GAUSSIAN_TRAINING;

  }



  const canonicalValues = Object.values(CANONICAL_FAILED_AT);

  if (canonicalValues.includes(s)) return s;



  const fromVerbose = stageFromVerboseFailureText(s);

  if (fromVerbose) return fromVerbose;



  const { progressMessage, percent: pct, explicitStageIndex } = options;

  if (typeof explicitStageIndex === 'number' && explicitStageIndex >= 0 && explicitStageIndex <= 3) {

    return canonicalLabelFromStageIndex(explicitStageIndex);

  }



  const msg = (progressMessage || '').toLowerCase();



  if (msg.includes('cancelled')) return CANONICAL_FAILED_AT.CANCELLED;

  if (

    msg.includes('structure')

    || msg.includes('sfm')

    || msg.includes('3d reconstruction')

    || (Number.isFinite(pct) && pct >= 5 && pct < 51)

  ) {

    return CANONICAL_FAILED_AT.SFM;

  }

  if (msg.includes('generating') || msg.includes('gaussian') || (Number.isFinite(pct) && pct >= 51)) {

    return CANONICAL_FAILED_AT.GAUSSIAN_TRAINING;

  }

  if (msg.includes('extract') || msg.includes('frame') || (Number.isFinite(pct) && pct >= 3 && !options?.framesExtracted)) {

    return CANONICAL_FAILED_AT.FRAME_EXTRACTION;

  }

  if (msg.includes('upload')) return CANONICAL_FAILED_AT.UPLOAD;



  if (lower.includes('during ')) {

    const duringMatch = s.match(/during\s+([A-Za-z0-9]+)/i);

    if (duringMatch?.[1]) {

      const nested = canonicalFailedAtFromRaw(duringMatch[1], options);

      if (nested !== CANONICAL_FAILED_AT.UNKNOWN) return nested;

    }

  }

  if (lower.includes('no healthy gpu') || lower.includes('gpu worker')) {

    return CANONICAL_FAILED_AT.DISPATCH;

  }

  if (
    isWatchdogGenericFailureDetail(s)
    || (options?.framesExtracted != null && Number(options.framesExtracted) > 0)
  ) {
    const ctx = resolveFailureStageFromProcessingContext({
      proc: {
        stage: options?.processingStage,
        framesExtracted: options?.framesExtracted,
        framesRegistered: options?.framesRegistered,
      },
      progressPercent: pct,
      progressMessage: options?.progressMessage,
    });
    return ctx.failedAtRaw;
  }

  if (Number.isFinite(pct) || msg) {

    return canonicalLabelFromStageIndex(failedStageIndexFromPercent(Number(pct) || 0));

  }



  return CANONICAL_FAILED_AT.UNKNOWN;

}



/** Dispatch / unknown failures must not inherit a high progress percent as Gaussian Training. */
export function failedAtSkipsPercentFallback(failedAt) {
  const n = String(failedAt || '').trim().toLowerCase();
  return n === 'dispatch' || n === 'unknown';
}



/** Clamp a terminal failure percent to the holistic band for its stage index. */
export function clampFailedPercentForStageIndex(stageIndex, percent) {
  const p = Number.isFinite(Number(percent)) ? Math.round(Number(percent)) : 0;
  if (typeof stageIndex !== 'number' || stageIndex < 0) return Math.max(0, Math.min(100, p));
  if (stageIndex === 0) return Math.min(p, 3);
  if (stageIndex === 1) return Math.min(Math.max(p, 3), 5);
  if (stageIndex === 2) return Math.min(Math.max(p, 5), 50);
  if (stageIndex === 3) return Math.min(Math.max(p, 51), 92);
  return Math.max(0, Math.min(100, p));
}



export function coerceFailedStageIndex(explicit, failedAt, percent) {

  if (failedAtSkipsPercentFallback(failedAt)) {
    return -1;
  }

  const fromLabel = failedStageIndexFromLabel(failedAt);

  if (fromLabel >= 0) return fromLabel;

  if (typeof explicit === 'number' && Number.isFinite(explicit)) {

    if (explicit >= 0 && explicit <= 3) return explicit;

    if (explicit >= 1 && explicit <= 5) {

      return explicit >= 5 ? 3 : explicit - 1;

    }

    if (explicit === -1) {

      return failedStageIndexFromPercent(percent);

    }

  }

  return failedStageIndexFromPercent(percent);

}



export function fallbackFailedPercent(explicit, percent) {

  if (typeof explicit === 'number' && Number.isFinite(explicit)) {

    return Math.round(Math.max(0, Math.min(100, explicit)));

  }

  if (Number.isFinite(percent)) {

    return Math.round(Math.max(0, Math.min(100, percent)));

  }

  return 0;

}



export function computeElapsedProcesstime(createdAt, existingProcesstime, nowMs = Date.now()) {

  if (typeof existingProcesstime === 'number' && Number.isFinite(existingProcesstime) && existingProcesstime > 0) {

    return Math.round(existingProcesstime);

  }

  const createdMs = timestampToMs(createdAt);

  if (createdMs == null) return 1;

  const elapsed = Math.round((nowMs - createdMs) / 1000);

  return Math.max(1, elapsed);

}



function normalizeFailureInputs(input) {

  let failedAtRaw = input.failedAtRaw != null ? String(input.failedAtRaw).trim() : null;

  let failureDetail = input.failureDetail != null ? String(input.failureDetail).trim() : null;



  if (!failureDetail && failedAtRaw && isVerboseFailureText(failedAtRaw)) {

    failureDetail = failedAtRaw;

    const fromVerbose = stageFromVerboseFailureText(failureDetail);

    if (fromVerbose) {

      failedAtRaw = fromVerbose;

    } else if (

      typeof input.failedStageIndex === 'number'

      && input.failedStageIndex >= 0

      && input.failedStageIndex <= 3

    ) {

      failedAtRaw = canonicalLabelFromStageIndex(input.failedStageIndex);

    }

  }



  const stageInferencePercent =

    typeof input.failedPercent === 'number' && Number.isFinite(input.failedPercent)

      ? Number(input.failedPercent)

      : (Number.isFinite(input.progressPercent) ? Number(input.progressPercent) : 0);



  return { failedAtRaw, failureDetail, stageInferencePercent };

}



export function buildFailureMetadata(input) {

  const { failedAtRaw, failureDetail, stageInferencePercent } = normalizeFailureInputs(input);

  const failedAt = input.status === 'cancelled'

    ? CANONICAL_FAILED_AT.CANCELLED

    : canonicalFailedAtFromRaw(failedAtRaw ?? null, {

      progressMessage: input.progressMessage,

      percent: stageInferencePercent,

      explicitStageIndex: input.failedStageIndex,

    });

  const failedPercent = fallbackFailedPercent(input.failedPercent, stageInferencePercent);

  const failedStageIndex = coerceFailedStageIndex(input.failedStageIndex, failedAt, failedPercent);

  const clampedFailedPercent = clampFailedPercentForStageIndex(failedStageIndex, failedPercent);

  const processtime = computeElapsedProcesstime(input.createdAt, input.processtime);



  const out = { failedAt, failedStageIndex, failedPercent: clampedFailedPercent, processtime };

  const detail = (failureDetail || '').trim();

  if (detail && detail !== failedAt) {

    out.failureDetail = detail.slice(0, 4000);

  }

  return out;

}



export function buildFailureFirestorePatch(input, extra = {}) {

  const meta = buildFailureMetadata(input);

  const patch = {

    'processing.status': input.status,

    'processing.processtime': meta.processtime,

    'processing.failedPercent': deleteField(),

    ...extra,

  };

  if (meta.failedAt != null && meta.failedStageIndex != null) {

    patch['processing.failedAt'] = meta.failedAt;

    patch['processing.failedStageIndex'] = meta.failedStageIndex;

  } else {

    patch['processing.failedAt'] = deleteField();

    patch['processing.failedStageIndex'] = deleteField();

  }

  if (meta.failureDetail) {

    patch['processing.failureDetail'] = meta.failureDetail;

  }

  return patch;

}


